import React from 'react';
import { View } from 'react-native';
import { Switch } from '@/components/ui/switch';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { ScheduleSettings } from '@/types/appointment';
import { usePlanFeature } from '@/hooks/use-plan-feature';

interface Props {
  value: ScheduleSettings;
  onChange: (patch: Partial<ScheduleSettings>) => void;
}

function Stepper({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  suffix,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <View className="flex-row items-center gap-2.5">
      <PressableScale
        haptic
        disabled={value <= min}
        onPress={() => onChange(Math.max(min, value - step))}
        className="h-8 w-8 items-center justify-center rounded-full border border-neutral-200"
        style={{ opacity: value <= min ? 0.4 : 1 }}
      >
        <Ionicons className="text-neutral-900" name="remove" size={16} />
      </PressableScale>
      <ThemedText className="min-w-14 text-center text-sm font-bold text-neutral-900">
        {value}
        {suffix ? ` ${suffix}` : ''}
      </ThemedText>
      <PressableScale
        haptic
        disabled={value >= max}
        onPress={() => onChange(Math.min(max, value + step))}
        className="h-8 w-8 items-center justify-center rounded-full border border-neutral-200"
        style={{ opacity: value >= max ? 0.4 : 1 }}
      >
        <Ionicons className="text-neutral-900" name="add" size={16} />
      </PressableScale>
    </View>
  );
}

function Row({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <View className="flex-row items-center justify-between gap-3 py-3">
      <View className="min-w-0 flex-1">
        <ThemedText className="text-sm font-semibold text-neutral-900">{title}</ThemedText>
        {hint ? <ThemedText className="text-xs text-neutral-500">{hint}</ThemedText> : null}
      </View>
      {children}
    </View>
  );
}

/** Antecedência, limites e lembrete. Controlado. */
export function RulesEditor({ value, onChange }: Props) {
  const whatsappPlan = usePlanFeature('WHATSAPP');
  return (
    <View
      className="rounded-2xl border border-neutral-100 bg-card px-4"
      style={{ borderCurve: 'continuous' }}
    >
      <Row title="Antecedência p/ marcar" hint="Mínimo antes do horário">
        <Stepper
          value={Math.round(value.minBookingLeadMinutes / 60)}
          onChange={(h) => onChange({ minBookingLeadMinutes: h * 60 })}
          min={0}
          max={72}
          suffix="h"
        />
      </Row>

      <View className="h-px bg-neutral-100" />
      <Row title="Antecedência p/ cancelar" hint="Janela do cliente cancelar/remarcar">
        <Stepper
          value={Math.round(value.minCancelLeadMinutes / 60)}
          onChange={(h) => onChange({ minCancelLeadMinutes: h * 60 })}
          min={0}
          max={168}
          suffix="h"
        />
      </Row>

      <View className="h-px bg-neutral-100" />
      <Row title="Horizonte" hint="Até quantos dias à frente o cliente vê">
        <Stepper
          value={value.bookingHorizonDays}
          onChange={(d) => onChange({ bookingHorizonDays: d })}
          min={1}
          max={365}
          step={5}
          suffix="d"
        />
      </Row>

      <View className="h-px bg-neutral-100" />
      <Row title="Máx. por cliente" hint="Avulsos futuros por cliente">
        <Stepper
          value={value.maxFuturePerClient}
          onChange={(n) => onChange({ maxFuturePerClient: n })}
          min={1}
          max={50}
        />
      </Row>

      <View className="h-px bg-neutral-100" />
      <Row
        title="Lembrete anti-falta"
        hint={
          whatsappPlan.locked
            ? 'Aviso no WhatsApp: dos planos pagos'
            : 'Aviso no WhatsApp antes do horário'
        }
      >
        <Switch
          checked={value.reminderEnabled && !whatsappPlan.locked}
          onCheckedChange={(on) => {
            if (on && !whatsappPlan.guard()) return;
            onChange({ reminderEnabled: on });
          }}
        />
      </Row>

      {value.reminderEnabled && !whatsappPlan.locked && (
        <>
          <View className="h-px bg-neutral-100" />
          <Row title="Antecedência do lembrete">
            <Stepper
              value={Math.round(value.reminderLeadMinutes / 60)}
              onChange={(h) => onChange({ reminderLeadMinutes: h * 60 })}
              min={1}
              max={72}
              suffix="h"
            />
          </Row>
        </>
      )}
    </View>
  );
}
